import {
  DialogContent,
  DialogOverlay,
  DialogPortal,
  Root as DialogRoot,
  DialogTrigger,
} from "@radix-ui/react-dialog";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import { AxiosError } from "axios";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  gradientRowBgClass,
  referralCtaButtonClass,
  shadowAndBorderClass,
} from "screens/Dashboard/utils/classes";
import { authApi } from "services/auth";
import { claimService } from "services/claim";
import { useUserStore } from "store/user";
import { useWindowSize } from "usehooks-ts";
import { useAccount } from "wagmi";
import usdtIcon from "../../../assets/icons/usdtIcon.png";
import CrossCloseIcon from "assets/svgComponents/buyTokenForm/CrossCloseIcon";
import { CircleDollarSign } from "lucide-react";
import { AttsIcon } from "assets/svgComponents/PresaleBlock/ConnectWallet copy";
import { appStore } from "store";
import {
  AnimatedGradientBorder,
  GlowCard,
} from "screens/Landing/components/shared";

export function ClaimButton() {
  const [loading, setLoading] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);
  const [convertedAmount, setConvertedAmount] = useState(0);
  const { isConnected, address } = useAccount();
  const { t } = useTranslation();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isClaimChoiceOpen, setIsClaimChoiceOpen] = useState(false);
  const [claimedAmount, setClaimedAmount] = useState(0);

  const { user } = useUserStore();
  const { currentRound } = appStore();

  const { width } = useWindowSize();
  const isMobile = width < 1024;

  const referralEarningUsd = user?.referralEarningUsd ?? 0;

  const attsBaseAmount =
    user?.referralEarningUsd && currentRound?.tokenPrice
      ? user?.referralEarningUsd / currentRound?.tokenPrice
      : 0;

  const attsBonusAmount = attsBaseAmount ? attsBaseAmount * 1.2 : 0;

  const claimText = loading ? t("Claiming...") : t("Claim in USDT");

  const convertText = convertLoading
    ? t("Converting...")
    : t("Convert to ATTS");

  const mainTriggerText = isMobile
    ? t("Claim Earnings")
    : t("Claim Referral Earnings");

  const handleOpenChoiceModal = () => {
    if (address && isConnected && referralEarningUsd > 0) {
      setIsClaimChoiceOpen(true);
    }
  };

  const handleClaim = async () => {
    if (!address || !isConnected || !referralEarningUsd) return;

    try {
      setLoading(true);

      await claimService.createClaimRequest(
        address,
        referralEarningUsd.toString(),
      );

      await authApi.getUser();
      setClaimedAmount(referralEarningUsd);

      setIsSuccessModalOpen(true);

      setIsClaimChoiceOpen(false);
    } catch (e) {
      if (e instanceof AxiosError) {
        console.log("claim error:", e?.response?.data?.message);
      } else {
        console.log("claim error:", e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!address || !isConnected) return;

    try {
      setConvertLoading(true);
      const convertedData = await claimService.convertEarnings();

      await authApi.getUser();
      if (convertedData?.amount) {
        setConvertedAmount(Number(convertedData?.amount));
        setIsSuccessModalOpen(true);

        setIsClaimChoiceOpen(false);
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        console.log("convert error:", e?.response?.data?.message);
      } else {
        console.log("convert error:", e);
      }
    } finally {
      setConvertLoading(false);
    }
  };

  if (!address || !isConnected) return null;

  return (
    <DialogRoot>
      <div
        className={clsx(
          "flex items-center justify-between gap-[8px] max-lg:flex-col max-lg:gap-[8px] max-lg:p-[8px] max-lg:pl-[16px] pl-[16px] lg:pr-[8px] max-lg:rounded-[12px] max-lg:before:!rounded-[12px] lg:py-[8px] rounded-[16px] before:!rounded-[16px]",
          "bg-[#161616]",
          shadowAndBorderClass,
        )}
      >
        <button
          type="button"
          disabled={!referralEarningUsd}
          onClick={handleOpenChoiceModal}
          className={clsx(
            referralCtaButtonClass,
            "min-w-0 w-auto max-w-full max-lg:w-full text-left",
          )}
        >
          <span className="font-luxora-medium font-normal text-black text-[16px] max-lg:text-[16px] tracking-[0] leading-[110%] whitespace-nowrap">
            {mainTriggerText}
          </span>
          <CircleDollarSign className="w-[18px] h-[18px] max-lg:w-[18px] max-lg:h-[18px] text-black shrink-0" />
        </button>
        <div
          className={clsx(
            "flex items-center gap-[4px] max-lg:gap-[8px] max-lg:text-14m text-[18px] p-[4px] max-lg:p-[5px] max-lg:pr-[8px] pr-[12px] max-lg:rounded-[8px] max-lg:before:!rounded-[8px] rounded-[8px] before:!rounded-[8px] font-luxora-regular bg-[#252525] leading-[110%] text-white flex-shrink-0 max-lg:w-full",
            shadowAndBorderClass,
          )}
        >
          <div
            className={clsx(
              "w-[23px] h-[23px] max-lg:w-[24px] max-lg:h-[24px] bg-[#252525] flex items-center justify-center rounded-full ",
              shadowAndBorderClass,
            )}
          >
            <img
              src={usdtIcon}
              className="h-[24px] max-lg:w-[24px] max-lg:h-[24px] rounded-full"
              alt=""
            />
          </div>
          <div className="text-[14px] max-lg:text-14m font-luxora-regular text-white leading-[110%]">
            {user.referralEarningUsd
              ? user.referralEarningUsd.toFixed(2)
              : (0).toFixed(2)}{" "}
            <span className="text-[14px] max-lg:text-14m  text-white/40 leading-[110%]">
              {t("USDT")}
            </span>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isSuccessModalOpen && (
          <DialogPortal forceMount>
            <DialogOverlay asChild>
              <motion.div className="fixed inset-0 z-[499] bg-black/50" />
            </DialogOverlay>
            <DialogContent className="!fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[500] w-[445px] max-lg:w-[360px] max-lg:max-h-[80vh] h-auto rounded-[28px] max-lg:rounded-[28px] outline-none">
              <AnimatedGradientBorder className="rounded-[28px] max-lg:rounded-[28px] w-full">
                <GlowCard className="flex flex-col h-full max-lg:rounded-[28px] max-lg:before:!rounded-[28px] rounded-[28px] before:!rounded-[28px] bg-[#111111] overflow-hidden p-[12px] max-lg:p-[12px] ring-1 ring-inset ring-white/[0.08]">
                  <motion.div
                    className={clsx(
                      "flex justify-between max-lg:mb-[24px] items-center max-lg:rounded-[16px] max-lg:before:!rounded-[15px] rounded-[16px] before:!rounded-[16px] p-[8px] max-lg:p-[9px] max-lg:pl-[12px] pl-[16px]",
                      gradientRowBgClass,
                    )}
                  >
                    <h2 className="text-white font-luxora-medium text-18 max-lg:text-20m">
                      {t("claim.requestSubmitted")}
                    </h2>
                  </motion.div>
                  <div
                    className={clsx(
                      "flex flex-col items-center rounded-[16px] mt-[24px] max-lg:mt-[24px] max-lg:rounded-[12px] gap-[8px] max-lg:gap-[8px] before:!rounded-[16px] max-lg:before:!rounded-[12px] bg-[#161616] p-[8px] max-lg:p-[8px] w-full",
                      shadowAndBorderClass,
                    )}
                  >
                    {claimedAmount ? (
                      <>
                        <img
                          src={usdtIcon}
                          className="w-[68px] h-[68px] max-lg:w-[70px] max-lg:h-[68px] rounded-full"
                          alt=""
                        />
                        <div className="text-16 max-lg:text-16m font-luxora-medium text-white leading-[110%]">
                          {claimedAmount ? claimedAmount.toFixed(2) : 0}{" "}
                          <span className="text-16 max-lg:text-16m  text-white/40 leading-[110%]">
                            {t("USDT")}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          className={clsx(
                            "w-[68px] h-[68px] max-lg:w-[70px] max-lg:h-[68px] rounded-full before:!rounded-full bg-[#252525] flex items-center justify-center",
                            shadowAndBorderClass,
                          )}
                        >
                          <AttsIcon className="w-[68px] h-[68px] max-lg:w-[70px] max-lg:h-[68px] " />
                        </div>
                        <div className="text-16 max-lg:text-16m font-luxora-medium text-white leading-[110%]">
                          {convertedAmount ? convertedAmount.toFixed(2) : 0}{" "}
                          <span className="text-16 max-lg:text-16m  text-white/40 leading-[110%]">
                            {t("ATTS")}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  {claimedAmount ? (
                    <div
                      className="text-[16px] font-luxora-regular leading-[110%] max-lg:p-[12px] max-lg:rounded-[12px] max-lg:text-16m p-[12px] border-[2px] text-center
                      rounded-[16px] border-white/[0.06] text-white/40 max-lg:my-[12px] mt-[12px] mb-[24px] "
                    >
                      {t("Your reward will be sent to")} {address}{" "}
                      {t("wallet.deliveryMessage")}
                    </div>
                  ) : (
                    <div
                      className="text-[16px] font-luxora-regular leading-[110%] max-lg:p-[12px] max-lg:rounded-[12px] max-lg:text-16m p-[12px] border-[2px] text-center
                    rounded-[16px] border-white/[0.06] text-white/40 max-lg:my-[12px] mt-[12px] mb-[24px] "
                    >
                      Converted $ATTS was added to your Purchased balance.
                      Additionally, 20% bonus was added to your Bonus balance.
                    </div>
                  )}

                  <DialogTrigger asChild>
                    <motion.button
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      onClick={() => {
                        setIsSuccessModalOpen(false);
                        setClaimedAmount(0);
                        setConvertedAmount(0);
                      }}
                      className="flex justify-center items-center max-lg:rounded-[12px] rounded-[16px] p-[16px] max-lg:p-[15px] bg-white hover:opacity-90 transition-opacity border-none shadow-[0px_6px_7.5px_0px_#0000000f]"
                    >
                      <p className="text-black text-center font-luxora-medium text-16 max-lg:text-16m">
                        {t("Continue")}
                      </p>
                    </motion.button>
                  </DialogTrigger>
                </GlowCard>
              </AnimatedGradientBorder>
            </DialogContent>
          </DialogPortal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isClaimChoiceOpen && (
          <DialogPortal forceMount>
            <DialogOverlay asChild>
              <motion.div className="fixed inset-0 z-[499] bg-black/50" />
            </DialogOverlay>
            <DialogContent className="!fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[500] w-[445px] max-lg:w-[360px] max-lg:max-h-[80vh] h-auto max-lg:min-h-[488px] rounded-[28px] max-lg:rounded-[28px] outline-none">
              <AnimatedGradientBorder className="rounded-[28px] max-lg:rounded-[28px] w-full">
                <GlowCard className="flex flex-col h-full max-lg:rounded-[28px] max-lg:before:!rounded-[28px] rounded-[28px] before:!rounded-[28px] bg-[#111111] overflow-hidden p-[12px] max-lg:p-[12px] ring-1 ring-inset ring-white/[0.08]">
                  <motion.div
                    className={clsx(
                      "flex justify-between items-center max-lg:rounded-[16px] max-lg:before:!rounded-[16px] rounded-[16px] before:!rounded-[16px] p-[8px] max-lg:p-[9px] max-lg:pl-[12px] pl-[16px]",
                      gradientRowBgClass,
                    )}
                  >
                    <h2 className="text-white font-luxora-medium text-18 max-lg:text-20m">
                      {t("Claim Referral Earnings")}
                    </h2>

                    <motion.button
                      onClick={() => setIsClaimChoiceOpen(false)}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className={clsx(
                        "w-[39px] h-[39px] max-lg:before:!rounded-[12px] max-lg:w-[39px] max-lg:h-[39px] max-lg:rounded-[12px] flex items-center justify-center before:!rounded-[12px] rounded-[12px] bg-[#111111]",
                        shadowAndBorderClass,
                      )}
                    >
                      <CrossCloseIcon
                        className="max-lg:w-[23px] max-lg:h-[23px] h-[20px] w-[20px]"
                        fill="white"
                      />
                    </motion.button>
                  </motion.div>
                  <div className="flex-1 overflow-y-auto hide-scrollbar max-lg:pb-[12px]">
                    <div
                      className={clsx(
                        "flex flex-col lg:text-[16px] font-luxora-regular p-[12px] leading-[110%]",
                        " my-[24px] text-white/40 sm:text-[16px] items-center rounded-[16px] max-lg:rounded-[12px] gap-[8px] ",
                        "max-lg:gap-[8px] before:!rounded-[16px] max-lg:before:!rounded-[12px] bg-transaparent max-lg:p-[8px] w-full",
                        shadowAndBorderClass,
                      )}
                    >
                      {t(
                        "Choose claiming method. If you convert them to $ATTS you get 20% more value.",
                      )}
                    </div>
                    <div className="flex gap-[12px] max-lg:flex-col h-[216px] max-lg:gap-[12px] lg:h-auto">
                      <div
                        className={clsx(
                          "flex flex-col lg:text-[14px] pt-[20px] pb-[8px] sm:text-[16px]",
                          "before:!rounded-[16px] max-lg:before:!rounded-[12px] bg-[#161616] p-[8px] max-lg:p-[8px] w-full",
                          "items-center rounded-[16px] max-lg:rounded-[12px] gap-[23px] max-lg:gap-[8px]",
                          shadowAndBorderClass,
                        )}
                      >
                        <img
                          src={usdtIcon}
                          className="w-[68px] h-[68px] max-lg:w-[70px] max-lg:h-[68px] rounded-full"
                          alt="USDT Icon"
                        />
                        <div className="text-16 max-lg:text-16m font-luxora-medium text-white leading-[110%]">
                          {referralEarningUsd.toFixed(2)}{" "}
                          <span className="text-16 max-lg:text-16m text-white/40 leading-[110%]">
                            USDT
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={handleClaim}
                          disabled={
                            loading ||
                            convertLoading ||
                            !isConnected ||
                            !address ||
                            !user?.referralEarningUsd
                          }
                          className="mt-auto w-full max-lg:text-16m cursor-pointer text-black text-16 font-luxora-medium leading-[110%] py-[15px] max-lg:py-[12px] rounded-[16px] max-lg:rounded-[12px] bg-white border-none shadow-[0px_6px_7.5px_0px_#0000000f] transition-opacity hover:opacity-90 disabled:opacity-55"
                        >
                          {claimText}
                        </button>
                      </div>

                      <div
                        className={clsx(
                          "flex flex-col lg:text-[14px] pt-[20px] pb-[8px] sm:text-[16px]",
                          "before:!rounded-[16px] max-lg:before:!rounded-[12px] bg-[#161616] p-[8px] max-lg:p-[8px] w-full",
                          "items-center rounded-[16px] max-lg:rounded-[12px] gap-[12px] max-lg:gap-[8px]",
                          shadowAndBorderClass,
                        )}
                      >
                        <div
                          className={clsx(
                            "w-[68px] h-[68px] max-lg:w-[70px] max-lg:h-[68px] rounded-full before:!rounded-full bg-[#252525] flex items-center justify-center",
                            shadowAndBorderClass,
                          )}
                        >
                          <AttsIcon className="w-[68px] h-[68px] max-lg:w-[70px] max-lg:h-[68px] " />
                        </div>
                        <div
                          className={clsx(
                            "flex gap-[4px] flex-col items-center",
                          )}
                        >
                          <div className="text-16 max-lg:text-16m font-luxora-medium text-white leading-[110%]">
                            {attsBonusAmount.toFixed(2)}{" "}
                            <span className="text-16 max-lg:text-16m font-luxora-medium text-white/40 leading-[110%]">
                              $ATTS
                            </span>
                          </div>
                          <div
                            className={clsx(
                              "text-16 max-lg:text-16m font-luxora-medium text-white/40 relative leading-[110%]",
                              "strikethrough-bolder-custom",
                            )}
                          >
                            {attsBaseAmount.toFixed(2)} ATTS{" "}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleConvert}
                          disabled={
                            convertLoading ||
                            !isConnected ||
                            !address ||
                            !user?.referralEarningUsd
                          }
                          className="mt-auto w-full cursor-pointer text-black text-16 max-lg:text-16m font-luxora-medium leading-[110%] py-[15px] max-lg:py-[12px] rounded-[16px] max-lg:rounded-[12px] bg-white border-none shadow-[0px_6px_7.5px_0px_#0000000f] transition-opacity hover:opacity-90 disabled:opacity-55"
                        >
                          {convertText}
                        </button>
                      </div>
                    </div>
                  </div>
                </GlowCard>
              </AnimatedGradientBorder>
            </DialogContent>
          </DialogPortal>
        )}
      </AnimatePresence>
    </DialogRoot>
  );
}
